import { useTranslate } from "@tolgee/react";
import { useMemo, useState } from "react";
import {
	type PortfolioBucket,
	type PortfolioPeriod,
	useHyperPortfolio,
} from "#/hooks/use-hyper-portfolio";
import { toNumber } from "#/lib/formatters";

export type TimeRange = "24H" | "1W" | "1M" | "All";
export type Product = "combined" | "perp";
export type Metric = "pnl" | "accountValue";

export type ChartPoint = { t: number; v: number };

const TIME_RANGES: TimeRange[] = ["24H", "1W", "1M", "All"];

const TIME_RANGE_TO_BUCKET: Record<
	TimeRange,
	Extract<PortfolioPeriod, "day" | "week" | "month" | "allTime">
> = {
	"24H": "day",
	"1W": "week",
	"1M": "month",
	All: "allTime",
};

function bucketKeyFor(timeRange: TimeRange, product: Product): PortfolioPeriod {
	const base = TIME_RANGE_TO_BUCKET[timeRange];
	if (product === "combined") return base;
	return `perp${base.charAt(0).toUpperCase()}${base.slice(1)}` as PortfolioPeriod;
}

function toSeries(
	bucket: PortfolioBucket | undefined,
	metric: Metric,
): ChartPoint[] {
	if (!bucket) return [];
	const raw = metric === "pnl" ? bucket.pnlHistory : bucket.accountValueHistory;
	return raw.map(([t, v]) => ({ t, v: toNumber(v) }));
}

export function usePortfolioChart() {
	const { t } = useTranslate();

	const [timeRange, setTimeRange] = useState<TimeRange>("All");
	const [product, setProduct] = useState<Product>("combined");
	const [metric, setMetric] = useState<Metric>("pnl");

	const { byPeriod, isLoading, isError } = useHyperPortfolio();

	const bucket = useMemo<PortfolioBucket | undefined>(() => {
		if (!byPeriod) return undefined;
		return byPeriod[bucketKeyFor(timeRange, product)];
	}, [byPeriod, timeRange, product]);

	const series = useMemo(() => toSeries(bucket, metric), [bucket, metric]);

	const volume = bucket ? toNumber(bucket.vlm) : 0;

	const currentValue = series.length > 0 ? series[series.length - 1].v : 0;

	const { max, min } = useMemo(() => {
		if (series.length === 0) {
			return { max: null as ChartPoint | null, min: null as ChartPoint | null };
		}
		let hi = series[0];
		let lo = series[0];
		for (const p of series) {
			if (p.v > hi.v) hi = p;
			if (p.v < lo.v) lo = p;
		}
		return { max: hi, min: lo };
	}, [series]);

	const productOptions: { value: Product; label: string }[] = [
		{ value: "combined", label: t("portfolio.chart.product.combined") },
		{ value: "perp", label: t("portfolio.chart.product.perp-only") },
	];
	const metricOptions: { value: Metric; label: string }[] = [
		{ value: "pnl", label: "PnL" },
		{ value: "accountValue", label: t("portfolio.chart.metric.account-value") },
	];
	const timeRangeOptions = TIME_RANGES.map((v) => ({
		value: v,
		label: v === "All" ? t("portfolio.chart.range.all") : v,
	}));

	const currentColor =
		currentValue > 0
			? "text-trade-green"
			: currentValue < 0
				? "text-trade-red"
				: "text-foreground";

	const timeRangeLabel =
		timeRange === "All" ? t("portfolio.chart.range.all") : timeRange;
	const productLabel =
		product === "combined"
			? t("portfolio.chart.product.combined")
			: t("portfolio.chart.product.perp-only");
	const metricLabel =
		metric === "pnl" ? "PnL" : t("portfolio.chart.metric.account-value");
	const headerRightLabel = `${timeRangeLabel} ${metricLabel} (${productLabel})`;

	const gradientOffset =
		max && min && max.v !== min.v
			? Math.max(0, Math.min(1, max.v / (max.v - min.v)))
			: currentValue >= 0
				? 1
				: 0;

	return {
		isLoading,
		isError,
		series,
		volume,
		currentValue,
		max,
		min,
		filters: {
			timeRange,
			product,
			metric,
			setTimeRange,
			setProduct,
			setMetric,
		},
		options: {
			timeRange: timeRangeOptions,
			product: productOptions,
			metric: metricOptions,
		},
		currentColor,
		metricLabel,
		headerRightLabel,
		gradientOffset,
	};
}
